/**
 * Route-level SEO/AEO metadata.
 *
 * Consumed by:
 *   1. React pages via <SEO> component (client-side title/head).
 *   2. `scripts/generate-static-pages.js` (postbuild) — writes per-URL
 *      static HTML with the same content baked into the `<head>` and a
 *      meaningful `<noscript>` body. That is what crawlers and AI answer
 *      engines see when JavaScript is disabled.
 *
 * Every field is optional except `path`, `title`, `description`, `h1`.
 * Extras: `aeoAnswer` (1–2 sentence AEO block), `sections` (H2 + copy),
 * `faq` ([{q,a}]), `jsonLd` (extra @graph objects), `noindex` (bool).
 */

export const BASE_URL = 'https://amppredict.com';

const positioning =
  'AMP Predict is a mock prediction market simulator for educational and forecasting practice using virtual TK¢ tokens. No real money is wagered.';

const ORGANIZATION_LD = {
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'AMP Predict',
  alternateName: 'ANJO LC',
  url: BASE_URL,
  logo: `${BASE_URL}/brand/amppredict-logo.png`,
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'amp@anjolc.com',
    contactType: 'customer service',
  },
};

const WEBSITE_LD = {
  '@type': 'WebSite',
  '@id': `${BASE_URL}/#website`,
  url: BASE_URL,
  name: 'AMP Predict',
  description: positioning,
  publisher: { '@id': `${BASE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const softwareLD = ({
  applicationCategory = 'EducationalApplication',
  operatingSystem = 'Web Browser',
  installUrl = BASE_URL,
} = {}) => ({
  '@type': 'SoftwareApplication',
  name: 'AMP Predict',
  applicationCategory,
  operatingSystem,
  url: BASE_URL,
  installUrl,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  description: positioning,
  publisher: { '@id': `${BASE_URL}/#organization` },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.6',
    reviewCount: '184',
  },
});

const breadcrumbLD = (crumbs) => ({
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: `${BASE_URL}${c.path}`,
  })),
});

// ─────────────────────────── Shared content blocks ──────────────────────────
const HOMEPAGE_FAQ = [
  {
    q: 'What is AMP Predict?',
    a: `${positioning} Users forecast real-world events by buying YES or NO positions with simulated TK¢ tokens across categories such as politics, sports, technology, economics, and world events.`,
  },
  {
    q: 'How do virtual prediction markets work?',
    a: 'Every market is a binary contract that pays 100 TK¢ if the event resolves YES and 0 TK¢ if it resolves NO. The current YES price (0–100¢) reflects the community\'s implied probability. If YES trades at 65¢, the market implies a 65% chance of the event occurring.',
  },
  {
    q: 'Is AMP Predict gambling or investing?',
    a: 'No. AMP Predict is a mock prediction market simulator for educational and forecasting practice using virtual tokens. It is not gambling, betting, real-money trading, investment advice, or a guaranteed prediction tool.',
  },
  {
    q: 'What are TK¢ tokens?',
    a: 'TK¢ (TrueKnowledge Cents) is the simulated in-app currency. TK¢ cannot be withdrawn, converted to real money, or used outside AMP Predict. New users receive a starter balance to begin practicing.',
  },
  {
    q: 'What categories are available?',
    a: 'Politics, sports, technology, economics, and world events. New markets are added continuously as the community submits ideas.',
  },
];

const HOMEPAGE_SECTIONS = [
  {
    h2: 'What AMP Predict is',
    body: `${positioning} We give forecasters a low-stakes sandbox to build calibration, test hypotheses, and learn how event contracts price uncertainty — the same mechanics used by real prediction markets, but with zero real-money risk.`,
  },
  {
    h2: 'How virtual prediction markets work',
    body: 'Every market resolves YES or NO. Prices sit between 0¢ and 100¢ and reflect the community\'s implied probability. Buying a YES contract at 40¢ costs 40 TK¢ and pays out 100 TK¢ if the event happens — so the price directly encodes the crowd\'s current forecast.',
  },
  {
    h2: 'Why users practice forecasting',
    body: 'Prediction markets sharpen probabilistic thinking. Traders learn to distinguish confidence from certainty, spot base-rate errors, and calibrate estimates against real outcomes. AMP Predict removes financial risk so learners can iterate freely.',
  },
  {
    h2: 'Example categories',
    body: 'Explore live markets across politics (elections, legislation), sports (NFL, NBA, MLB, NHL, Premier League, UFC, F1), technology (AI, big tech), economics (rates, GDP, inflation), and world events (geopolitics, diplomacy). Every market is publicly viewable and updated in real time.',
  },
  {
    h2: 'How YES/NO contracts work',
    body: 'A YES contract pays 100 TK¢ if the event resolves YES and 0 TK¢ otherwise. A NO contract is the mirror image. If you believe the market is under-pricing an outcome, you buy the corresponding contract. The difference between your entry price and 100 TK¢ (or 0) is your virtual profit or loss.',
  },
  {
    h2: 'All trading uses simulated tokens only',
    body: 'AMP Predict never accepts wagers of real money on markets. Subscription fees fund platform access; they never fund a wallet. Every buy, sell, position, and payout is denominated in TK¢ — a play-money unit with no cash value and no legal or financial claim.',
  },
  {
    h2: 'Download the AMP Predict app',
    body: 'AMP Predict is available on the web at amppredict.com and as a native mobile app on the Apple App Store (iOS) and Google Play Store (Android). Same account, same markets, same virtual TK¢ balance across every device.',
  },
];

// ─────────────────────────── Learn hub content ──────────────────────────────
const LEARN_HUB_ARTICLES = [
  {
    slug: 'what-is-a-prediction-market',
    title: 'What Is a Prediction Market?',
    summary:
      'A prediction market is a binary contract whose price reflects the crowd\'s probability estimate of a future event. This guide explains the mechanics.',
  },
  {
    slug: 'what-is-a-mock-prediction-market',
    title: 'What Is a Mock Prediction Market?',
    summary:
      'A mock prediction market simulator like AMP Predict uses virtual tokens to give users the full mechanics of event contract trading without real-money risk.',
  },
  {
    slug: 'how-yes-no-event-contracts-work',
    title: 'How YES / NO Event Contracts Work',
    summary:
      'YES contracts pay 100 TK¢ if the event resolves YES and 0 otherwise. Learn how pricing, orderbooks, and settlement work end to end.',
  },
  {
    slug: 'how-implied-probability-works',
    title: 'How Implied Probability Works',
    summary:
      'A YES contract trading at 65¢ implies a 65% probability of the event happening. This article covers implied probability, calibration, and common pitfalls.',
  },
  {
    slug: 'how-to-practice-forecasting-without-real-money',
    title: 'How to Practice Forecasting Without Real Money',
    summary:
      'AMP Predict lets you build calibrated forecasting habits using virtual TK¢ tokens — no financial risk, no gambling, just structured practice.',
  },
];

// ─────────────────────────── Route table ────────────────────────────────────
export const ROUTES = {
  '/': {
    path: '/',
    title: 'AMP Predict | Mock Prediction Market Simulator',
    description:
      'AMP Predict is a mock prediction market simulator where users forecast real-world events using virtual TK¢ tokens. Practice event forecasting across politics, sports, technology, economics, and world events with no real-money risk.',
    keywords:
      'prediction market simulator, mock prediction market, virtual event forecasting, YES NO event contracts, forecasting practice, AMP Predict, TK¢, no real money',
    h1: 'Practice Forecasting Real-World Events With Virtual Prediction Markets',
    aeoAnswer: positioning,
    sections: HOMEPAGE_SECTIONS,
    faq: HOMEPAGE_FAQ,
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      softwareLD(),
      {
        '@type': 'HowTo',
        name: 'How to create a prediction market on AMP Predict',
        description:
          'Submit a binary yes/no question with a closing date and resolution criteria. After admin review, the market goes live and the community can trade YES/NO contracts on your question.',
        totalTime: 'PT5M',
        supply: [{ '@type': 'HowToSupply', name: 'An AMP Predict account (Google or email/password)' }],
        tool: [{ '@type': 'HowToTool', name: 'A web browser' }],
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Open the creation flow', text: "Click the 'CREATE YOUR OWN MARKET' banner on the homepage or the 'MARKETS CREATION' tab in the nav. If you are not logged in, the sign-in modal appears.", url: `${BASE_URL}/ideas?create=1` },
          { '@type': 'HowToStep', position: 2, name: 'Log in or register', text: 'Sign in with Google or your email and password. New users can register with an email and password in seconds.' },
          { '@type': 'HowToStep', position: 3, name: 'Write your market question', text: 'Phrase a clear, unambiguous yes/no question. Add a description that states the exact resolution criteria and trusted source that will be used to decide the outcome.' },
          { '@type': 'HowToStep', position: 4, name: 'Pick a category and closing date', text: 'Choose a category and subcategory (e.g. Crypto → Bitcoin, Sports → NFL). Select the date the event will be decided — this is also when the market auto-closes for trading.' },
          { '@type': 'HowToStep', position: 5, name: 'Submit for review', text: 'Submit the idea. An admin reviews for clarity and tradability, usually within 24 hours. Once approved, your market goes live — you and the community can trade YES or NO contracts on it until the closing date.' },
        ],
      },
    ],
  },
  '/about': {
    path: '/about',
    title: 'About AMP Predict | Who We Are',
    description:
      'AMP Predict is a mock prediction market simulator built by ANJO LC. Learn about the team, mission, and why we built a virtual event-forecasting sandbox for learners and analysts.',
    keywords: 'about AMP Predict, ANJO LC, mock prediction market team, virtual forecasting sandbox',
    h1: 'Meet the team building the leading virtual prediction market',
    aeoAnswer: positioning,
    sections: [
      {
        h2: 'Our mission',
        body: 'AMP Predict exists to make probabilistic thinking accessible. We built a mock prediction market simulator so anyone can practice forecasting real-world events without financial risk. Every market resolves YES or NO, priced 0–100¢, and settled from public sources.',
      },
      {
        h2: 'How we\'re different',
        body: 'Unlike real-money prediction markets, AMP Predict never accepts wagers of cash on markets. Every trade is denominated in TK¢ — a simulated token with no cash value. That makes AMP Predict an educational tool, not a gambling or investment platform.',
      },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])],
  },
  '/how-it-works': {
    path: '/how-it-works',
    title: 'How AMP Predict Works | Virtual Event Forecasting',
    description:
      'Learn how AMP Predict works: sign up, browse or create YES/NO markets on real-world events, buy positions with virtual TK¢, and settle when events resolve — all with no real money.',
    keywords: 'how AMP Predict works, virtual event forecasting, YES NO contracts explained, mock prediction market tutorial',
    h1: 'From sign-up to your first trade in under 60 seconds',
    aeoAnswer: positioning,
    sections: [
      {
        h2: '1. Create a free account',
        body: 'Sign up with Google, Apple, or email. New users receive a virtual TK¢ starter balance instantly — no payment info required. Free access includes browsing every live market, previewing prices, and reading resolution criteria.',
      },
      {
        h2: '2. Browse or propose a market',
        body: 'Explore hundreds of live markets across politics, sports, technology, economics, and world events. Anything the community can measure and settle from public sources qualifies. If you can\'t find the question you want, propose your own — approved ideas go live for everyone to trade.',
      },
      {
        h2: '3. Trade YES or NO with virtual tokens',
        body: 'Every market has a live orderbook. Place limit or market orders on YES or NO contracts using virtual TK¢. Prices are 0–100¢ and represent implied probability. Watch positions update in real time as the community trades.',
      },
      {
        h2: '4. Settle when the event resolves',
        body: 'When a market\'s closing date passes and the outcome is decided from public sources, holders of the winning side receive 100 TK¢ per contract. Losing contracts settle at 0. Ties or unresolvable outcomes refund 50 TK¢ per contract to all holders.',
      },
    ],
    faq: [
      { q: 'Do I need real money to use AMP Predict?', a: 'No. AMP Predict is a mock prediction market. All balances and payouts are virtual TK¢ tokens.' },
      { q: 'How long does market approval take?', a: 'Newly proposed markets are typically reviewed and approved within 24 hours if the resolution criteria are clear and settleable from public sources.' },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'How It Works', path: '/how-it-works' }])],
  },
  '/markets': {
    path: '/markets',
    title: 'Prediction Markets | AMP Predict',
    description:
      'Browse live mock prediction markets across politics, sports, technology, economics, and world events. Trade YES/NO event contracts with virtual TK¢ tokens on AMP Predict.',
    keywords: 'prediction markets, YES NO markets, event contracts, virtual forecasting, mock markets',
    h1: 'Forecast real-world events with virtual tokens',
    aeoAnswer:
      'AMP Predict lists hundreds of live YES/NO prediction markets across politics, sports, technology, economics, and world events. Every market is priced 0–100¢ and settled in virtual TK¢ tokens — no real money is wagered.',
    sections: [
      { h2: 'Politics', body: 'Elections, legislation, and government decisions. Explore the politics category to trade YES/NO contracts on political outcomes.' },
      { h2: 'Sports', body: 'NFL, NBA, MLB, NHL, Premier League, UFC, F1, and more. Live orderbooks on match and season outcomes across all major leagues.' },
      { h2: 'Technology', body: 'AI milestones, big-tech product launches, and startup outcomes. Forecast the pace of innovation with virtual tokens.' },
      { h2: 'Economics', body: 'Interest rates, GDP releases, inflation prints, and macro indicators. Practice reading economic signals with no cash risk.' },
      { h2: 'World Events', body: 'Geopolitics, diplomacy, and global affairs. Track how the community prices high-uncertainty world events.' },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Markets', path: '/markets' }])],
  },
  '/learn': {
    path: '/learn',
    title: 'Learn Prediction Markets | AMP Predict Forecasting Guide',
    description:
      'Learn how prediction markets work: YES/NO event contracts, implied probability, calibration, and how to practice forecasting risk-free with virtual TK¢ tokens on AMP Predict.',
    keywords: 'learn prediction markets, YES NO contracts, implied probability, calibration, forecasting guide',
    h1: 'Everything you need to understand event forecasting',
    aeoAnswer:
      'This is the AMP Predict learning hub. Read short guides on prediction market mechanics, YES/NO event contracts, implied probability, and how to build calibrated forecasting habits using a mock (virtual-token) prediction market.',
    sections: LEARN_HUB_ARTICLES.map((a) => ({
      h2: a.title,
      body: a.summary,
      href: `/learn/${a.slug}`,
    })),
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Learn', path: '/learn' }])],
  },
  '/app': {
    path: '/app',
    title: 'AMP Predict App | Mock Prediction Markets on iOS and Android',
    description:
      'Download the AMP Predict mobile app to practice virtual prediction market trading on iOS and Android. Same account, same markets, same virtual TK¢ balance across every device.',
    keywords: 'AMP Predict app, prediction market app, iOS app, Android app, mobile forecasting',
    h1: 'AMP Predict App — iOS and Android',
    aeoAnswer:
      'The AMP Predict mobile app brings virtual prediction market trading to iPhone and Android. Sign in with the same account you use on the web and trade YES/NO event contracts with virtual TK¢ tokens on the go.',
    sections: [
      { h2: 'iOS — Apple App Store', body: 'Download AMP Predict from the Apple App Store. Requires iOS 15 or later. Works on iPhone and iPad.', href: 'https://apps.apple.com/us/app/amppredict/id6764166197' },
      { h2: 'Android — Google Play', body: 'Download AMP Predict from the Google Play Store. Requires Android 8.0 or later.', href: 'https://play.google.com/store/apps/details?id=com.anjolc.ampredict' },
      { h2: 'One account, everywhere', body: 'Your virtual TK¢ balance, positions, orders, and watchlists sync in real time across web, iOS, and Android.' },
    ],
    jsonLd: [
      breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'App', path: '/app' }]),
      softwareLD({ applicationCategory: 'EducationalApplication', operatingSystem: 'iOS, Android', installUrl: `${BASE_URL}/app` }),
    ],
  },
  '/ios': {
    path: '/ios',
    title: 'AMP Predict for iOS | Prediction Markets on iPhone',
    description:
      'Download AMP Predict on the Apple App Store. Practice YES/NO event contract forecasting with virtual TK¢ tokens on iPhone and iPad — no real money required.',
    keywords: 'AMP Predict iOS, iPhone prediction market app, iPad forecasting app, Apple App Store',
    h1: 'Practice forecasting on iPhone or iPad',
    aeoAnswer:
      'AMP Predict is available on iPhone and iPad through the Apple App Store. Download the free app to practice YES/NO event forecasting with virtual TK¢ tokens.',
    sections: [
      { h2: 'Get AMP Predict on iPhone or iPad', body: 'AMP Predict is a free download on the Apple App Store. Requires iOS 15 or later.', href: 'https://apps.apple.com/us/app/amppredict/id6764166197' },
      { h2: 'What you can do on iOS', body: 'Browse live markets, place limit and market orders on YES/NO contracts, receive push notifications when markets resolve, and manage subscriptions via In-App Purchase.' },
    ],
    jsonLd: [
      breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'iOS App', path: '/ios' }]),
      softwareLD({ applicationCategory: 'EducationalApplication', operatingSystem: 'iOS', installUrl: 'https://apps.apple.com/us/app/amppredict/id6764166197' }),
    ],
  },
  '/android': {
    path: '/android',
    title: 'AMP Predict for Android | Google Play Prediction App',
    description:
      'Download AMP Predict on Google Play. Practice YES/NO event contract forecasting with virtual TK¢ tokens on your Android phone or tablet — no real money required.',
    keywords: 'AMP Predict Android, Google Play prediction market, Android forecasting app',
    h1: 'Practice forecasting on any Android device',
    aeoAnswer:
      'AMP Predict is available on Android devices through Google Play. Download the free app to practice YES/NO event forecasting with virtual TK¢ tokens.',
    sections: [
      { h2: 'Get AMP Predict on Android', body: 'AMP Predict is a free download on Google Play. Requires Android 8.0 or later.', href: 'https://play.google.com/store/apps/details?id=com.anjolc.ampredict' },
      { h2: 'What you can do on Android', body: 'Browse live markets, place limit and market orders on YES/NO contracts, receive push notifications when markets resolve, and manage subscriptions via Google Play Billing.' },
    ],
    jsonLd: [
      breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Android App', path: '/android' }]),
      softwareLD({ applicationCategory: 'EducationalApplication', operatingSystem: 'Android', installUrl: 'https://play.google.com/store/apps/details?id=com.anjolc.ampredict' }),
    ],
  },
  '/contact': {
    path: '/contact',
    title: 'Contact AMP Predict | Support & Feedback',
    description:
      'Contact the AMP Predict team for support, feedback, partnership, or press inquiries. Email ampcontactus@anjolc.com — replies typically within 1–2 business days.',
    keywords: 'contact AMP Predict, ANJO LC support, mock prediction market support, response time, office hours',
    h1: 'Get in touch — support, feedback, and press inquiries',
    aeoAnswer:
      'You can reach the AMP Predict team by emailing ampcontactus@anjolc.com or using the contact form on this page. Our support desk is online Monday–Friday, 9:00 AM – 6:00 PM Eastern, and we reply to most messages within 1–2 business days.',
    sections: [
      { h2: 'Response times & office hours', body: 'AMP Predict is operated by ANJO LC out of Maryland, USA. The support team is online Monday–Friday, 9:00 AM – 6:00 PM Eastern. Most email inquiries receive a reply within 1–2 business days; billing and refund requests submitted through the Billing page are processed within 3 business days.' },
      { h2: 'What to include in your message', body: 'To help us respond faster, please include your account email, a clear category (support, feedback, bug, refund, partnership, or press), and a description. For bugs, add steps to reproduce and your device / browser version.' },
      { h2: 'Billing & refunds', body: 'Billing and refund requests are managed from the Billing page in your profile. Prorated refunds are available within 7 days of the most recent payment; older payments are non-refundable. See the Refund Policy for full terms.' },
      { h2: 'Account & data', body: 'Delete your account and all associated data at any time from Profile → Settings → Delete Account. For GDPR / CCPA data-export requests, email us and we will send an export within 30 days.' },
      { h2: 'Press & partnerships', body: 'For press or partnership proposals — including educational institutions, forecasting clubs, and research groups — email ampcontactus@anjolc.com with "PRESS" or "PARTNERSHIP" in the subject line.' },
    ],
    faq: [
      { q: 'How fast will I get a reply?', a: 'Most email inquiries are answered within 1–2 business days (Monday–Friday, 9 AM – 6 PM Eastern).' },
      { q: 'Where is AMP Predict based?', a: 'AMP Predict is built and operated by ANJO LC, a professional services firm headquartered in Maryland, USA.' },
      { q: 'How do I request a refund?', a: 'Open Profile → Billing and click "Request refund" within 7 days of the most recent payment.' },
      { q: 'How do I delete my account?', a: 'From Profile → Settings → Delete Account. Deletion is immediate and removes all personally identifiable information from the production database.' },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])],
  },
  '/privacy': {
    path: '/privacy',
    title: 'Privacy Policy | AMP Predict',
    description:
      'AMP Predict Privacy Policy. Learn what data we collect, how we use it, what we share (nothing with data brokers), and your rights including account deletion.',
    keywords: 'AMP Predict privacy policy, ANJO LC privacy, data collection, no tracking',
    h1: 'How AMP Predict handles your data and protects your privacy',
    aeoAnswer:
      'AMP Predict does not track users across other apps or websites, does not sell data to data brokers, and lets users delete their account and data at any time from Profile Settings.',
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Privacy', path: '/privacy' }])],
  },
  '/eula': {
    path: '/eula',
    title: 'Terms of Use (EULA) | AMP Predict',
    description:
      'AMP Predict End User License Agreement. Full EULA covering license grant, virtual currency, subscriptions, Apple App Store terms, and Apple standard EULA.',
    keywords: 'AMP Predict EULA, end user license agreement, Apple standard EULA',
    h1: 'AMP Predict Terms of Use (EULA)',
    aeoAnswer:
      'This is the End User License Agreement for AMP Predict. It covers your license to use the app, virtual TK¢ tokens, subscription terms, and the Apple standard EULA for iOS installations.',
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'EULA', path: '/eula' }])],
  },
  '/terms': {
    // /terms is aliased to /eula in the SPA (client-side <Navigate />).
    // The pre-rendered HTML shipped here still contains the Terms of
    // Service body so crawlers landing on /terms see meaningful content,
    // and the canonical link points at /eula so search engines consolidate
    // ranking signals to a single URL.
    path: '/terms',
    canonical: `${BASE_URL}/eula`,
    title: 'Terms of Service | AMP Predict',
    description:
      'AMP Predict Terms of Service — governs your use of the app, virtual TK¢ tokens, subscriptions, and the Apple standard EULA. Canonical URL: /eula.',
    keywords: 'AMP Predict terms of service, TOS, terms and conditions',
    h1: 'Your agreement with AMP Predict when using the platform',
    aeoAnswer:
      'These are the AMP Predict Terms of Service. They govern your use of the AMP Predict platform, the virtual TK¢ currency, paid subscriptions, and the Apple standard EULA for iOS installs. The full canonical document lives at /eula.',
    sections: [
      { h2: 'License grant', body: 'AMP Predict grants you a limited, revocable, non-exclusive license to use the platform for personal, non-commercial forecasting practice with virtual TK¢ tokens only.' },
      { h2: 'Virtual currency', body: 'TK¢ (TrueKnowledge Cents) has no cash value, cannot be redeemed, and cannot be transferred off-platform. All market outcomes settle in TK¢.' },
      { h2: 'Subscriptions', body: 'Paid tiers are billed via Stripe (web) or the App Store / Play Store (mobile). Manage or cancel from Profile Settings; App Store IAP subscriptions must be cancelled in iOS Settings.' },
      { h2: 'Governing law', body: 'These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-law principles.' },
      { h2: 'Full agreement', body: 'The full binding End User License Agreement, including the Apple standard EULA, is available at /eula.', href: '/eula' },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Terms of Service', path: '/terms' }])],
  },
  '/refund-policy': {
    path: '/refund-policy',
    title: 'Refund Policy | AMP Predict Subscription Refunds',
    description:
      'AMP Predict Refund Policy for paid subscription tiers. Prorated refunds can be requested within 7 days of payment via the Billing page.',
    keywords: 'AMP Predict refund policy, subscription refund, cancel subscription',
    h1: 'How subscription refunds work at AMP Predict',
    aeoAnswer:
      'AMP Predict offers prorated refunds on paid subscriptions requested within 7 days of the most recent payment. Refund requests are submitted from the Billing page and processed via Stripe after admin review.',
    sections: [
      { h2: 'Eligibility window', body: 'Refunds are available for the most recent subscription payment if requested within 7 days of the charge. Older payments are non-refundable.' },
      { h2: 'How to request', body: 'Open Profile → Billing and click "Request refund". Include a short reason so admins can review and approve quickly. You will receive a confirmation email once processed.' },
      { h2: 'Processing time', body: 'Approved refunds are processed via Stripe (web) within 3–5 business days. App Store / Play Store purchases must be refunded through Apple or Google — see their respective help pages.' },
      { h2: 'What is not refundable', body: 'Virtual TK¢ balances, individual market losses, and any spend on IAP consumables outside of the subscription itself.' },
    ],
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Refund Policy', path: '/refund-policy' }])],
  },
  '/changelog': {
    path: '/changelog',
    title: 'Changelog | AMP Predict Product Updates',
    description:
      'A chronological log of every notable AMP Predict release — new features, improvements, and fixes across the web app, iOS app, and Android app.',
    keywords: 'AMP Predict changelog, product updates, release notes, prediction market app updates',
    h1: 'AMP Predict Changelog',
    aeoAnswer:
      'This is the official AMP Predict changelog. Every notable release across the web app, iOS app, and Android app is logged here with the date, category (new, improved, fixed), and a short description of what changed.',
    noindex: true,
    jsonLd: [breadcrumbLD([{ name: 'Home', path: '/' }, { name: 'Changelog', path: '/changelog' }])],
  },
};

export const LEARN_ARTICLES = LEARN_HUB_ARTICLES;
export { positioning, ORGANIZATION_LD, WEBSITE_LD, softwareLD, breadcrumbLD };
